import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { httpClient } from '../services/httpClient';
import { ENDPOINTS } from '../config/api.config';
import DataVerificationTooltip from './DataVerificationTooltip';
import ServiceFormModal from './ServiceFormModal';
import '../styles/admin.css';

/* ── Get pricing type display (icon + label) ──────────────────────── */
const getPricingTypeDisplay = (pricingType) => {
  const pricingMap = {
    visiting: { icon: '🚗', label: 'Visiting Charges' },
    fixed: { icon: '✅', label: 'Fixed Price' },
    starting: { icon: '📈', label: 'Starting Price' },
    subscription: { icon: '🔄', label: 'Subscription' },
  };
  return pricingMap[pricingType] || { icon: '💰', label: 'Price' };
};

const AdminServices = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [selectedService, setSelectedService] = useState(null);
    const [sortBy, setSortBy] = useState('id');
    const [sortOrder, setSortOrder] = useState('asc');

    // Listen for location changes (navigate back from edit)
    useEffect(() => {
        fetchServices();
    }, [location]);

    // Initial load
    useEffect(() => {
        fetchServices();
    }, []);

    const fetchServices = async () => {
        try {
            setLoading(true);
            // Don't use cache when coming from an update (location.state.refreshServices)
            const useCache = !location.state?.refreshServices;
            const response = await httpClient.get(`${ENDPOINTS.ADMIN_API}/services`, null, { useCache });
            // Handling both paginated and flat array responses
            setServices(response?.data?.data || response?.data || []);
        } catch (error) {
            console.error('Error fetching services:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('⚠️ Are you sure you want to delete this service? This action cannot be undone.')) return;

        try {
            await httpClient.delete(`${ENDPOINTS.ADMIN_API}/services/${id}`);
            alert('Service deleted successfully');
            fetchServices(); // Refresh list
        } catch (error) {
            alert(error.response?.data?.message || 'Error deleting service');
        }
    };

    const openAddModal = () => {
        setModalMode('add');
        setSelectedService(null);
        setModalOpen(true);
    };

    const openEditModal = (service) => {
        setModalMode('edit');
        setSelectedService(service);
        setModalOpen(true);
    };

    const handleModalClose = () => {
        setModalOpen(false);
        setSelectedService(null);
    };

    const handleModalSuccess = () => {
        fetchServices();
    };

    const handleSort = (column) => {
        if (sortBy === column) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(column);
            setSortOrder('asc');
        }
    };

    const getSortArrow = (column) => {
        if (sortBy !== column) return ' ⇅';
        return sortOrder === 'asc' ? ' ↑' : ' ↓';
    };

    const sortData = (data) => {
        return [...data].sort((a, b) => {
            let aVal = a[sortBy];
            let bVal = b[sortBy];

            if (sortBy === 'user_id') {
                aVal = a.user?.name || '';
                bVal = b.user?.name || '';
            }

            if (typeof aVal === 'string') {
                aVal = aVal.toLowerCase();
                bVal = bVal.toLowerCase();
                return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
            }

            if (sortOrder === 'asc') {
                return aVal > bVal ? 1 : -1;
            } else {
                return aVal < bVal ? 1 : -1;
            }
        });
    };

    const filteredServices = services.filter(service =>
        service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        service.user?.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const sortedServices = sortData(filteredServices);

    return (
        <div className="admin-section">
            <div className="section-header">
                <div className="header-left">
                    <h2>🔧 Services Management</h2>
                    <p className="subtitle">Total Services: {filteredServices.length}</p>
                </div>
                
                <button 
                    className="btn btn-primary"
                    onClick={openAddModal}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px' }}
                >
                    <span style={{ fontSize: '20px' }}>+</span> Add New Service
                </button>
            </div>

            <div className="search-bar-container" style={{ marginBottom: '20px' }}>
                <input
                    type="text"
                    className="admin-search-input"
                    placeholder="🔍 Search by service name or vendor..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                />
            </div>

            <div className="admin-table">
                <table>
                    <thead>
                        <tr>
                            <th style={{ width: '80px', cursor: 'pointer' }} onClick={() => handleSort('id')}>ID{getSortArrow('id')}</th>
                            <th style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>Service Name{getSortArrow('name')}</th>
                            <th style={{ cursor: 'pointer' }} onClick={() => handleSort('user_id')}>Vendor{getSortArrow('user_id')}</th>
                            <th style={{ cursor: 'pointer' }} onClick={() => handleSort('price')}>Price{getSortArrow('price')}</th>
                            <th>Pricing Type</th>
                            <th>Availability</th>
                            <th>Data Status</th>
                            <th style={{ textAlign: 'right' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="8" style={{ textAlign: 'center', padding: '40px' }}>
                                    <div className="loader">Loading Services...</div>
                                </td>
                            </tr>
                        ) : filteredServices.length === 0 ? (
                            <tr>
                                <td colSpan="8" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                                    No services found matching your search.
                                </td>
                            </tr>
                        ) : (
                            sortedServices.map(service => (
                                <tr key={service.id}>
                                    <td><span className="id-badge">#{service.id}</span></td>
                                    <td>
                                        <div style={{ fontWeight: '600', color: '#ffffff' }}>{service.name}</div>
                                        <div style={{ fontSize: '0.75rem', color: '#87888a' }}>{service.category?.name}</div>
                                    </td>
                                    <td>
                                        <div style={{ fontWeight: '500' }}>{service.user?.name || 'Unknown Vendor'}</div>
                                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{service.user?.email}</div>
                                    </td>
                                    <td>
                                        <span className="price-tag">${service.price}</span>
                                    </td>
                                    <td>
                                        <div style={{ fontSize: '0.85rem', fontWeight: '500' }}>
                                            {getPricingTypeDisplay(service.pricing_type).icon} {getPricingTypeDisplay(service.pricing_type).label}
                                        </div>
                                    </td>
                                    <td>
                                        <div style={{ fontSize: '0.8rem', maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {service.hours || 'Not Set'}
                                        </div>
                                    </td>
                                    <td>
                                        <DataVerificationTooltip dataVerification={service.data_verification}>
                                            {service.data_verification?.is_complete ? (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <span style={{ fontSize: '0.9rem' }}>✅</span>
                                                    <span style={{ color: '#10b981', fontWeight: '600', fontSize: '0.85rem' }}>Complete</span>
                                                    {service.data_verification?.completion_percentage && (
                                                        <span style={{ color: '#64748b', fontSize: '0.8rem' }}>
                                                            {service.data_verification.completion_percentage}%
                                                        </span>
                                                    )}
                                                </div>
                                            ) : (
                                                <span style={{ color: '#f97316', fontWeight: '600', fontSize: '0.85rem' }}>
                                                    Incomplete
                                                    {service.data_verification?.completion_percentage && (
                                                        <span style={{ color: '#64748b', fontSize: '0.8rem', marginLeft: '4px' }}>
                                                            {service.data_verification.completion_percentage}%
                                                        </span>
                                                    )}
                                                </span>
                                            )}
                                        </DataVerificationTooltip>
                                    </td>
                                    <td>
                                        <div className="actions" style={{ justifyContent: 'flex-end' }}>
                                            <button
                                                className="btn btn-edit"
                                                onClick={() => openEditModal(service)}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                className="btn btn-danger"
                                                onClick={() => handleDelete(service.id)}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <ServiceFormModal
                isOpen={modalOpen}
                mode={modalMode}
                serviceData={selectedService}
                onClose={handleModalClose}
                onSuccess={handleModalSuccess}
            />
        </div>
    );
};

export default AdminServices;